import React from 'react';
import FormularioMateriaPrima from './FormularioMateriaPrima';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';
import { useClienteDaProducao } from '../utils/clienteDaProducao';
import { ehPrincipal } from '../utils/responsavelPrincipal';
import PopupNovaPessoa from '../components/PopupNovaPessoa';
import FotoAutenticada from '../components/FotoAutenticada';

export default function FormularioTarefasDoProjeto({
  // Dados
  tarefa,
  setTarefa,
  responsaveisTarefa,
  setResponsaveisTarefa,
  horasTarefa,
  setHorasTarefa,
  responsaveis,
  
  // Funções
  apiCall,
  apiCallWithAlternatives,
  fetchTarefas,
  handleSalvar,
  handleRemoverResponsavel,
  onDefinirPrincipal, // marca/desmarca o responsável principal (edição)
  onPessoaCriada, // depois de "Adicionar nova pessoa": a tela recarrega a lista de pessoas
  
  // Estados adicionais
  adicionarRespAberto,
  setAdicionarRespAberto,
  novoResp,
  setNovoResp,
  handleAdicionarResponsavel,
  
  // Modo (criar, editar, detalhe)
  modo = 'editar', // 'criar' | 'editar' | 'detalhe'
  
  // Refs
  refFormularioMateriaPrima,

  // Fotos (ao criar e ao editar)
  selectedFiles,
  setSelectedFiles,
  handleFilesChange,
  onExcluirFoto,

  // Tipos de Tarefa
  tiposTarefa = [],
  onTiposTarefaRefresh,
  
  // Status gravado da tarefa (antes das alterações do formulário): concluir pergunta o que sobrou de matéria-prima
  statusOriginal = '',

  // Controles
  onFechar
}) {
  const [materiasPrimasAlocadas, setMateriasPrimasAlocadas] = React.useState([]);

  // "Adicionar nova pessoa" (null | 'criar' | 'editar'): cadastra sem sair da tela, recarrega a lista e já seleciona a pessoa
  const [novaPessoaAberta, setNovaPessoaAberta] = React.useState(null);
  const aoCriarPessoa = async (criada) => {
    if (onPessoaCriada) await onPessoaCriada();
    const id = criada?.id ?? criada?.Id;
    if (!id) return;
    if (novaPessoaAberta === 'editar') {
      setNovoResp && setNovoResp(prev => ({ ...prev, responsavelId: String(id) }));
      return;
    }
    const lista = tarefa.responsaveis?.length ? tarefa.responsaveis : [''];
    const vazia = lista.findIndex(p => !p);
    if (vazia >= 0) {
      setTarefa({ ...tarefa, responsaveis: lista.map((p, i) => (i === vazia ? String(id) : p)) });
    } else {
      setTarefa({ ...tarefa, responsaveis: [...lista, String(id)] });
      setHorasTarefa([...horasTarefa, { horasPrevistas: '', horasReais: '' }]);
    }
  };

  // Cliente vem da produção (não é editável)
  useClienteDaProducao(tarefa?.producaoId, clienteId => setTarefa(prev => ({ ...prev, id_cliente: clienteId })));

  // ── Estados do popup Gerenciar Tipo Tarefa ──────────────────────────────────
  const [tipoTarefaPopupAberto, setTipoTarefaPopupAberto] = React.useState(false);
  const [novoTipoNome, setNovoTipoNome] = React.useState('');
  const [tipoSelecionado, setTipoSelecionado] = React.useState(null); // id do tipo selecionado via checkbox
  const [criarTipoLoading, setCriarTipoLoading] = React.useState(false);
  const [deletarTipoLoading, setDeletarTipoLoading] = React.useState(false);
  const [tarefasVinculadasInfo, setTarefasVinculadasInfo] = React.useState(null); // { tipoNome, tarefas: [...] }

  const handleCriarTipoTarefa = async () => {
    if (!novoTipoNome.trim()) { alert('Informe o nome do tipo de tarefa'); return; }
    try {
      setCriarTipoLoading(true);
      await apiCall('TipoTarefa', 'POST', { nome: novoTipoNome.trim() });
      setNovoTipoNome('');
      if (onTiposTarefaRefresh) await onTiposTarefaRefresh();
    } catch (e) {
      alert('Erro ao criar tipo de tarefa: ' + e.message);
    } finally {
      setCriarTipoLoading(false);
    }
  };

  const handleExcluirTipoTarefa = async () => {
    if (!tipoSelecionado) return;
    const tipo = tiposTarefa.find(t => t.id === tipoSelecionado);
    if (!window.confirm(`Deseja realmente excluir o tipo "${tipo?.nome}"?`)) return;
    try {
      setDeletarTipoLoading(true);
      const urls = [
        `${BASE_URL}/TipoTarefa/${tipoSelecionado}`,
        `${BASE_URL}/TipoTarefa/${tipoSelecionado}`
      ];
      let lastError = null;
      let responded = false;
      for (const url of urls) {
        try {
          const res = await fetch(url, { method: 'DELETE', headers: { 'Content-Type': 'application/json', ...(getAuthToken() ? {'Authorization': 'Bearer ' + getAuthToken()} : {}) } });
          if (res.ok) {
            setTipoSelecionado(null);
            if (onTiposTarefaRefresh) await onTiposTarefaRefresh();
            responded = true;
            break;
          } else {
            const body = await res.json().catch(() => null);
            if (res.status === 400 && body && body.tarefas) {
              // Existem tarefas vinculadas → mostra popup de aviso
              setTarefasVinculadasInfo({ tipoNome: tipo?.nome || '', tarefas: body.tarefas });
              responded = true;
              break;
            }
            lastError = (body && (body.message || body.title)) || `Erro ${res.status}`;
          }
        } catch (e) { lastError = e.message; }
      }
      if (!responded) alert('Erro ao excluir tipo de tarefa: ' + lastError);
    } finally {
      setDeletarTipoLoading(false);
    }
  };

  React.useEffect(() => {
    if (modo === 'editar' && tarefa?.id) {
      carregarMateriasPrimasAlocadas(tarefa.id);
    }
  }, [modo, tarefa?.id]);

  const carregarMateriasPrimasAlocadas = async (tarefaId) => {
    if (!tarefaId) return;
    try {
      const result = await apiCall(`MateriaPrima/por-tarefa/${tarefaId}`);
      if (result && Array.isArray(result)) {
        setMateriasPrimasAlocadas(result);
        console.log('✅ Matérias-primas da tarefa carregadas:', result);
      } else {
        setMateriasPrimasAlocadas([]);
      }
    } catch (error) {
      console.log('ℹ️ Nenhuma matéria-prima encontrada para esta tarefa:', error.message);
      setMateriasPrimasAlocadas([]);
    }
  };

  // "Ver Detalhes" = o mesmo formulário da edição, com todos os dados, só para ver: tudo bloqueado e sem salvar
  const somenteLeitura = modo === 'detalhe';
  const isDetalhe = somenteLeitura;
  const isCriar = modo === 'criar';
  const isEditar = modo === 'editar' || somenteLeitura;

  const tituloModal = isCriar ? '➕ Criar Nova Tarefa' : somenteLeitura ? '✅ Tarefa - Detalhes' : '✏️ Editar Tarefa';
  const corTitulo = 'rgb(235, 133, 0)';

  return (
    <div className="modal-overlay" onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95020 }}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 800, background: '#fff', borderRadius: 12, padding: 20, maxHeight: '85vh', overflowY: 'auto' }}>
        
        {/* Cabeçalho */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, color: corTitulo }}>{tituloModal}</h3>
          <button onClick={onFechar} aria-label="Fechar" title="Fechar" style={{ backgroundColor: corTitulo, color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>×</button>
        </div>

        {/* Em "Ver Detalhes" tudo fica bloqueado (fieldset desabilitado); só o Fechar funciona */}
        <fieldset disabled={somenteLeitura} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

        {/* Formulário - MODO CRIAR/EDITAR (e detalhes, bloqueado) */}
        {(isCriar || isEditar) && (
          <div id="tarefa-form-dados" className="formulario-tarefas-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }}>
              <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Descrição {isCriar && '*'}</label>
              <textarea
                value={tarefa.descricao || ''}
                onChange={(e) => setTarefa({ ...tarefa, descricao: e.target.value })}
                style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', minHeight: 60 }}
                placeholder={isCriar ? "Descreva a tarefa..." : ""}
              />
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }}>
              <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Status</label>
              <select
                value={tarefa.status || 'Pendente'}
                onChange={(e) => setTarefa({ ...tarefa, status: e.target.value })}
                style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
              >
                <option value="">Selecione</option>
                <option value="Pendente">Pendente</option>
                <option value="Em Andamento">Em Andamento</option>
                <option value="Concluído">Concluído</option>
                <option value="Atrasado">Atrasado</option>
                <option value="Cancelado">Cancelado</option>
              </select>
            </div>

            {/* Tipo Tarefa Select + botão "+" */}
            <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }}>
              <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Tipo Tarefa</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select
                  value={tarefa.tipoTarefaId || ''}
                  onChange={(e) => setTarefa({ ...tarefa, tipoTarefaId: e.target.value ? parseInt(e.target.value) : null })}
                  style={{ flex: 1, padding: '10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}
                >
                  <option value="">Selecione um tipo...</option>
                  {tiposTarefa.map(tt => (
                    <option key={tt.id} value={tt.id}>{tt.nome}</option>
                  ))}
                </select>
                {!somenteLeitura && <button
                  type="button"
                  onClick={() => { setTipoTarefaPopupAberto(true); setTipoSelecionado(null); setNovoTipoNome(''); setTarefasVinculadasInfo(null); }}
                  title="Gerenciar Tipos de Tarefa"
                  style={{
                    width: 40, height: 40, borderRadius: 6,
                    backgroundColor: 'rgb(235, 133, 0)', color: 'white',
                    border: 'none', cursor: 'pointer',
                    fontSize: 22, fontWeight: 700, lineHeight: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0
                  }}
                >+</button>}
              </div>
            </div>

            {/* Responsáveis - SOMENTE NO MODO CRIAR: vários, cada um com horas reais; com mais de um, pode marcar o principal */}
            {isCriar && (() => {
              const lista = tarefa.responsaveis?.length ? tarefa.responsaveis : [''];
              const escolhidos = lista.filter(Boolean).length;
              const totalHorasPrevistas = lista.reduce((t, _, i) => t + (parseFloat(horasTarefa[i]?.horasPrevistas) || 0), 0);
              const atualizarLinhas = (novasPessoas, novasHoras, novoPrincipal) =>
                setTarefa({ ...tarefa, responsaveis: novasPessoas, responsavelPrincipalIndex: novoPrincipal });
              const adicionarLinha = () => {
                atualizarLinhas([...lista, ''], null, tarefa.responsavelPrincipalIndex ?? null);
                setHorasTarefa([...horasTarefa, { horasPrevistas: '', horasReais: '' }]);
              };
              const removerLinha = (index) => {
                const principal = tarefa.responsavelPrincipalIndex;
                // O principal acompanha a linha (se a removida era a principal, fica sem principal)
                const novoPrincipal = principal === index || principal == null ? null : (principal > index ? principal - 1 : principal);
                const novasPessoas = lista.filter((_, i) => i !== index);
                const novasHoras = horasTarefa.filter((_, i) => i !== index);
                atualizarLinhas(novasPessoas.length ? novasPessoas : [''], null, novoPrincipal);
                setHorasTarefa(novasHoras.length ? novasHoras : [{ horasPrevistas: '', horasReais: '' }]);
              };
              const campo = { padding: '10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 14 };
              return (
                <div id="tarefa-form-responsaveis" style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: 8, padding: 12, borderRadius: 8, background: '#fafafa', border: '1px solid #e0e0e0' }}>
                  <label style={{ fontSize: 13, fontWeight: 600 }}>Responsáveis *</label>
                  {lista.map((pessoaId, index) => (
                    <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <select
                        value={pessoaId}
                        onChange={(e) => {
                          const novas = [...lista];
                          novas[index] = e.target.value;
                          atualizarLinhas(novas, null, tarefa.responsavelPrincipalIndex ?? null);
                        }}
                        style={{ ...campo, flex: '1 1 200px', minWidth: 200 }}
                      >
                        <option value="">Selecione uma pessoa</option>
                        {responsaveis.map((pessoa, idx) => {
                          const id = pessoa.id || pessoa.Id || pessoa.PessoasId || pessoa.pessoasId || idx;
                          // A mesma pessoa não pode ser escolhida em duas linhas
                          const jaEscolhida = lista.some((p, i) => i !== index && String(p) === String(id));
                          return (
                            <option key={id} value={id} disabled={jaEscolhida}>
                              {pessoa.nome || pessoa.Nome || pessoa.PessoaNome || pessoa.pessoaNome}
                              {pessoa.custoHora ? ` (R$ ${parseFloat(pessoa.custoHora).toFixed(2)}/h)` : ''}
                            </option>
                          );
                        })}
                      </select>

                      {/* Na criação só horas previstas; as reais são lançadas ao concluir a tarefa */}
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        value={horasTarefa[index]?.horasPrevistas || ''}
                        onChange={(e) => {
                          const novasHoras = [...horasTarefa];
                          novasHoras[index] = { ...novasHoras[index], horasPrevistas: e.target.value };
                          setHorasTarefa(novasHoras);
                        }}
                        placeholder="Hrs Previstas"
                        title="Horas previstas (as reais são informadas ao concluir a tarefa)"
                        style={{ ...campo, width: 110 }}
                      />

                      {/* Com mais de um responsável, pode marcar um como principal (opcional; só um) */}
                      {escolhidos > 1 && pessoaId && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: tarefa.responsavelPrincipalIndex === index ? 'rgb(235, 133, 0)' : '#555', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={tarefa.responsavelPrincipalIndex === index}
                            onChange={() => setTarefa({ ...tarefa, responsavelPrincipalIndex: tarefa.responsavelPrincipalIndex === index ? null : index })}
                          />
                          Principal
                        </label>
                      )}

                      <button
                        type="button"
                        onClick={() => removerLinha(index)}
                        title="Remover este responsável"
                        style={{ padding: '6px 12px', backgroundColor: '#f44336', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={adicionarLinha}
                        style={{ padding: '8px 14px', backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                      >
                        + Adicionar responsável
                      </button>
                      <button
                        type="button"
                        onClick={() => setNovaPessoaAberta('criar')}
                        style={{ padding: '8px 14px', backgroundColor: '#fff', color: 'rgb(235, 133, 0)', border: '1px solid rgb(235, 133, 0)', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                      >
                        Adicionar nova pessoa
                      </button>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1565c0' }}>
                      Total de horas previstas: {totalHorasPrevistas.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}h
                    </span>
                  </div>
                </div>
              );
            })()}

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>ID Projeto</label>
              <input
                type="number"
                value={tarefa.producaoId || ''}
                readOnly
                title="O projeto é escolhida ao criar a tarefa e não pode ser alterada"
                style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                placeholder={isCriar ? "ID do projeto" : ""}
              />
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>ID Cliente</label>
              <input
                type="number"
                value={tarefa.id_cliente || ''}
                readOnly
                title="O cliente vem do projeto"
                style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                placeholder={isCriar ? "Preenchido pelo projeto" : ""}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Data Início Previsto</label>
              <input
                type="date"
                value={tarefa.dataInicioPrevisto ? tarefa.dataInicioPrevisto.split('T')[0] : ''}
                onChange={(e) => setTarefa({ ...tarefa, dataInicioPrevisto: e.target.value })}
                style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
                disabled={isEditar}
              />
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Data Fim Previsto</label>
              <input
                type="date"
                value={tarefa.dataFimPrevisto ? tarefa.dataFimPrevisto.split('T')[0] : ''}
                onChange={(e) => setTarefa({ ...tarefa, dataFimPrevisto: e.target.value })}
                style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
                disabled={isEditar}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Data Início Real</label>
              <input
                type="date"
                value={tarefa.dataInicioReal ? tarefa.dataInicioReal.split('T')[0] : ''}
                onChange={(e) => setTarefa({ ...tarefa, dataInicioReal: e.target.value })}
                style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
              />
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Data Fim Real</label>
              <input
                type="date"
                value={tarefa.dataFimReal ? tarefa.dataFimReal.split('T')[0] : ''}
                onChange={(e) => setTarefa({ ...tarefa, dataFimReal: e.target.value })}
                style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }}>
              <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Observações</label>
              <textarea
                value={tarefa.observacoes || ''}
                onChange={(e) => setTarefa({ ...tarefa, observacoes: e.target.value })}
                style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', minHeight: 60 }}
                placeholder={isCriar ? "Observações adicionais..." : ""}
              />
            </div>
          </div>
        )}

        {/* Container de Matérias-Primas (CRIAÇÃO e EDIÇÃO) */}
        {(isCriar || isEditar) && (
          <div id="tarefa-form-materias" style={{ marginTop: 20, padding: 15, backgroundColor: '#f9f9f9', borderRadius: 8, border: '1px solid #e0e0e0' }}>
            <h4 style={{ margin: '0 0 12px 0', color: 'rgb(235, 133, 0)', fontSize: 14 }}>Matérias-Primas</h4>
            <FormularioMateriaPrima 
              ref={refFormularioMateriaPrima}
              tarefaId={tarefa?.id}
              tipoId="tarefa"
              onMateriaPrimaAdded={(result) => {
                console.log('✅ Matéria-prima adicionada:', result);
              }}
              modoEdicao={isEditar}
              // Detalhes: só lista (sem adicionar nem alterar)
              ocultarAdicao={somenteLeitura}
              somenteLeitura={somenteLeitura}
              tarefaCancelada={!somenteLeitura && isEditar && /cancel/i.test(tarefa?.status || '') && !/cancel/i.test(statusOriginal || '')}
              tarefaConcluindo={!somenteLeitura && isEditar && /conclu/i.test(tarefa?.status || '') && !/conclu/i.test(statusOriginal || '')}
              onDestinoConfirmado={isEditar && !somenteLeitura ? handleSalvar : null}
            />
          </div>
        )}

        {/* Upload de Fotos - ao criar e ao editar; em Detalhes, só as salvas */}
        {(isEditar || isCriar) && (
          <div id="tarefa-form-fotos" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
            <label style={{ fontWeight: '500', fontSize: '14px', marginBottom: '10px', display: 'block' }}>
              Fotos da Tarefa
            </label>

            {/* Fotos Existentes (GET) */}
            {tarefa && tarefa.Fotos && tarefa.Fotos.length > 0 && (
              <div style={{ marginBottom: '15px' }}>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '8px' }}>
                  Fotos Salvas:
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {tarefa.Fotos.map((foto, idx) => {
                    const fotoId = foto.id || foto.Id;
                    const nomeArquivo = foto.nomeArquivo || foto.NomeArquivo || `Foto ${idx + 1}`;
                    return (
                      <div key={idx} style={{ position: 'relative', textAlign: 'center' }}>
                        <FotoAutenticada
                          src={`${BASE_URL}/TarefaFoto/${fotoId}`}
                          alt={nomeArquivo}
                          style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '5px', border: '2px solid #ddd' }}
                        />
                        {onExcluirFoto && !somenteLeitura && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Deseja realmente excluir esta foto?')) {
                                onExcluirFoto(fotoId);
                              }
                            }}
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              backgroundColor: '#f44336',
                              color: 'white',
                              border: '2px solid white',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                            title="Excluir foto"
                          >
                            ×
                          </button>
                        )}
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '4px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {nomeArquivo}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Upload de Novas Fotos (não aparece em Detalhes) */}
            {!somenteLeitura && (<>
            <div style={{ fontSize: '13px', fontWeight: '500', color: '#666', marginBottom: '8px' }}>
              Adicionar Novas Fotos:
            </div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFilesChange}
              style={{
                padding: '8px',
                borderRadius: '5px',
                border: '1px solid #ccc',
                width: '100%',
                fontSize: '14px'
              }}
            />
            {selectedFiles && selectedFiles.length > 0 && (
              <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {selectedFiles.map((file, idx) => (
                  <div key={idx} style={{ position: 'relative' }}>
                    <img
                      src={file.preview}
                      alt={`Preview ${idx + 1}`}
                      style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '5px', border: '2px solid rgb(235, 133, 0)' }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFiles(prev => {
                          try { URL.revokeObjectURL(file.preview); } catch {}
                          return prev.filter((_, i) => i !== idx);
                        });
                      }}
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: '2px solid white',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                      title="Remover foto"
                    >
                      ×
                    </button>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '4px', textAlign: 'center' }}>Nova</div>
                  </div>
                ))}
              </div>
            )}
            </>)}
          </div>
        )}

        {/* Container azul de Matérias-Primas Alocadas REMOVIDO - agora está apenas dentro do FormularioMateriaPrima */}

        {/* Container de Responsáveis (DETALHE e EDITAR) */}
        {(isDetalhe || isEditar) && (
          <div id="tarefa-form-responsaveis-vinculados" style={{ marginTop: 20, padding: 15, backgroundColor: '#f9f9f9', borderRadius: 8, border: '1px solid #e0e0e0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>Responsáveis Vinculados</h4>
              {isEditar && !somenteLeitura && (
                <button
                  onClick={() => setAdicionarRespAberto(true)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'rgb(235, 133, 0)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600
                  }}
                >
                  + Adicionar Responsável
                </button>
              )}
            </div>
            
            {responsaveisTarefa.length === 0 ? (
              <p style={{ margin: 0, color: '#999' }}>Nenhum responsável vinculado a esta tarefa.</p>
            ) : (
              <div style={{ display: 'grid', gap: 12, marginTop: 8 }}>
                {responsaveisTarefa.map((resp, idx) => {
                  const responsavelId = resp.id || resp.responsaveisId || resp.ResponsaveisId;
                  const horasResp = horasTarefa.find(h => h.responsavelId === responsavelId);
                  
                  return (
                    <div key={idx} className="responsavel-item-grid" style={{ 
                      padding: 14, 
                      backgroundColor: '#fafafa', 
                      borderRadius: 8, 
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      display: 'grid', 
                      gridTemplateColumns: isDetalhe ? '2fr 1.2fr 1.2fr 1.2fr 0.8fr' : '2fr 1.2fr 1.2fr 1.2fr 0.8fr', 
                      gap: 12, 
                      alignItems: 'end'
                    }}>
                      {/* Nome + Perfil */}
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ fontSize: 12, color: '#666', fontWeight: 700, marginBottom: 4 }}>Nome</label>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#000', lineHeight: 1.3 }}>{resp.nome || resp.Nome || '—'}</div>
                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{resp.perfil || resp.Perfil || 'Sem perfil'}</div>
                        {/* Responsável principal: só um por tarefa (com um só, ele já é o principal) */}
                        {isEditar && !somenteLeitura && onDefinirPrincipal ? (
                          <button
                            type="button"
                            onClick={() => onDefinirPrincipal(resp)}
                            title={ehPrincipal(resp) ? 'Clique para deixar de ser o principal' : 'Tornar este o responsável principal (só um)'}
                            style={{ marginTop: 6, alignSelf: 'flex-start', padding: '4px 10px', borderRadius: 12, border: '1px solid rgb(235, 133, 0)', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: ehPrincipal(resp) ? 'rgb(235, 133, 0)' : '#fff', color: ehPrincipal(resp) ? '#fff' : 'rgb(235, 133, 0)' }}
                          >
                            {ehPrincipal(resp) ? '⭐ Principal' : '☆ Tornar principal'}
                          </button>
                        ) : ehPrincipal(resp) && (
                          <div style={{ marginTop: 6, fontSize: 11, fontWeight: 700, color: 'rgb(235, 133, 0)' }}>⭐ Principal</div>
                        )}
                      </div>
                      
                      {/* Custo/Hora */}
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ fontSize: 12, color: '#666', fontWeight: 700, marginBottom: 4 }}>Custo/Hora</label>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1976d2' }}>
                          {((resp.custoHora || resp.CustoHora) || (resp.PessoaCustoHora || resp.pessoaCustoHora)) ? `R$ ${parseFloat(resp.custoHora || resp.CustoHora || resp.PessoaCustoHora || resp.pessoaCustoHora).toFixed(2)}/h` : '—'}
                        </div>
                      </div>
                      
                      {/* Horas: previstas (da criação) e reais (lançadas só ao concluir a tarefa) — aqui só para ver */}
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ fontSize: 12, color: '#666', fontWeight: 700, marginBottom: 4 }}>Hrs Previstas / Reais</label>
                        <div title="As horas reais são informadas ao concluir a tarefa" style={{ fontSize: 14, fontWeight: 600 }}>
                          <span style={{ color: '#1565c0' }}>{(parseFloat(horasResp?.horasPrevistas ?? resp.horasPrevistas ?? resp.HorasPrevistas) || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}h</span>
                          {' / '}
                          <span style={{ color: '#d32f2f' }}>{(parseFloat(horasResp?.horasReais ?? resp.horasReais ?? resp.HorasReais) || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}h</span>
                        </div>
                      </div>
                      
                      {/* Botão Remover (apenas em modo editar) */}
                      {isEditar && !somenteLeitura && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => handleRemoverResponsavel(resp)}
                            title="Remover"
                            style={{ backgroundColor: '#f44336', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Totalizador */}
            {horasTarefa.length > 0 && (
              <div style={{ marginTop: 15, padding: 12, backgroundColor: '#fff3e0', borderRadius: 6, border: '2px solid rgb(235, 133, 0)', display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <label style={{ fontSize: 12, color: '#666', fontWeight: 600, display: 'block', marginBottom: 4 }}>TOTAL HORAS PREVISTAS</label>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1565c0' }}>
                    {horasTarefa.reduce((sum, h) => sum + (parseFloat(h.horasPrevistas) || 0), 0).toFixed(1)}h
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <label style={{ fontSize: 12, color: '#666', fontWeight: 600, display: 'block', marginBottom: 4 }}>TOTAL HORAS REAIS</label>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#4CAF50' }}>
                    {horasTarefa.reduce((sum, h) => sum + (parseFloat(h.horasReais) || 0), 0).toFixed(1)}h
                  </div>
                  <div style={{ fontSize: 11, color: '#888' }}>lançadas ao concluir</div>
                </div>
              </div>
            )}
          </div>
        )}

        </fieldset>

        {/* Botões de Ação (em Detalhes, só o Fechar) */}
        <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'center' }}>
          {(isCriar || isEditar) && !somenteLeitura && (
            <button
              onClick={handleSalvar}
              style={{ padding: '12px 30px', backgroundColor: 'rgb(235, 133, 0)', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
            >
              {isCriar ? 'Criar' : 'Salvar'}
            </button>
          )}
          <button
            onClick={onFechar}
            style={{ padding: '12px 30px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
          >
            {isDetalhe ? 'Fechar' : 'Cancelar'}
          </button>
        </div>
      </div>

      {novaPessoaAberta && (
        <PopupNovaPessoa onFechar={() => setNovaPessoaAberta(null)} onCriada={aoCriarPessoa} />
      )}

      {/* ── Modal Gerenciar Tipos de Tarefa ─────────────────────────────────── */}
      {tipoTarefaPopupAberto && (
        <div onClick={() => setTipoTarefaPopupAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95040 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 520, background: '#fff', borderRadius: 12, padding: 24, maxHeight: '85vh', overflowY: 'auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>🏷️ Gerenciar Tipos de Tarefa</h3>
              <button onClick={() => setTipoTarefaPopupAberto(false)} aria-label="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer' }}>×</button>
            </div>

            {/* Criar novo tipo */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              <input
                type="text"
                value={novoTipoNome}
                onChange={(e) => setNovoTipoNome(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCriarTipoTarefa(); }}
                placeholder="Nome do novo tipo de tarefa..."
                style={{ flex: 1, padding: '10px', borderRadius: 6, border: '2px solid rgb(235, 133, 0)', fontSize: 13, outline: 'none' }}
              />
              <button
                onClick={handleCriarTipoTarefa}
                disabled={criarTipoLoading || !novoTipoNome.trim()}
                style={{ padding: '10px 18px', backgroundColor: 'rgb(235, 133, 0)', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', opacity: criarTipoLoading ? 0.7 : 1 }}
              >
                {criarTipoLoading ? '...' : 'Criar'}
              </button>
              <button
                onClick={() => { setNovoTipoNome(''); }}
                style={{ padding: '10px 14px', backgroundColor: '#757575', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
              >
                Limpar
              </button>
            </div>

            {/* Botão Excluir — só aparece quando um tipo está selecionado */}
            {tipoSelecionado && (
              <div style={{ marginBottom: 12 }}>
                <button
                  onClick={handleExcluirTipoTarefa}
                  disabled={deletarTipoLoading}
                  style={{ padding: '8px 20px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13, opacity: deletarTipoLoading ? 0.7 : 1 }}
                >
                  {deletarTipoLoading ? 'Excluindo...' : '🗑️ Excluir Tipo Selecionado'}
                </button>
              </div>
            )}

            {/* Listagem dos tipos existentes */}
            <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', backgroundColor: '#f5f5f5', fontWeight: 700, fontSize: 13, color: '#555', borderBottom: '1px solid #e0e0e0' }}>
                Tipos Cadastrados ({tiposTarefa.length})
              </div>
              {tiposTarefa.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>Nenhum tipo de tarefa cadastrado.</div>
              ) : (
                tiposTarefa.map((tt) => (
                  <label key={tt.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', backgroundColor: tipoSelecionado === tt.id ? '#fff3e0' : 'white', transition: 'background 0.1s' }}>
                    <input
                      type="radio"
                      name="tipoTarefaCheckbox"
                      checked={tipoSelecionado === tt.id}
                      onChange={() => setTipoSelecionado(tipoSelecionado === tt.id ? null : tt.id)}
                      style={{ width: 16, height: 16, accentColor: 'rgb(235, 133, 0)', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 14, color: '#333' }}>{tt.nome}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: '#999' }}>ID: {tt.id}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Popup "Tipo em uso — não pode excluir" ─────────────────────────── */}
      {tarefasVinculadasInfo && (
        <div onClick={() => setTarefasVinculadasInfo(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95050 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 600, background: '#fff', borderRadius: 12, padding: 24, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: '#f44336' }}>⚠️ Tipo em uso — impossível excluir</h3>
              <button onClick={() => setTarefasVinculadasInfo(null)} aria-label="Fechar" style={{ backgroundColor: '#f44336', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer' }}>×</button>
            </div>

            <div style={{ padding: '12px 16px', backgroundColor: '#fdecea', borderRadius: 8, marginBottom: 16, fontSize: 14, color: '#c62828' }}>
              O tipo <strong>"{tarefasVinculadasInfo.tipoNome}"</strong> não pode ser excluído pois existem{' '}
              <strong>{tarefasVinculadasInfo.tarefas.length}</strong> tarefa(s) vinculada(s) a ele.
            </div>

            <div style={{ fontWeight: 700, fontSize: 13, color: '#555', marginBottom: 10 }}>
              Tarefas que usam este tipo:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {tarefasVinculadasInfo.tarefas.map((t, idx) => (
                <div key={t.id || idx} style={{ padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: 8, backgroundColor: '#fafafa', fontSize: 13 }}>
                  <span style={{ fontWeight: 700, color: 'rgb(235, 133, 0)' }}>#{t.id}</span>
                  {' — '}
                  <span style={{ color: '#333' }}>{t.descricao || '—'}</span>
                  {' '}
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}>{t.status || '—'}</span>
                  {t.producaoId && <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>Prod. #{t.producaoId}</span>}
                </div>
              ))}
            </div>

            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center' }}>
              <button onClick={() => setTarefasVinculadasInfo(null)} style={{ padding: '12px 30px', backgroundColor: 'rgb(235, 133, 0)', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Adicionar Responsável (dentro do modal de edição) */}
      {adicionarRespAberto && (
        <div className="modal-overlay" onClick={() => setAdicionarRespAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95030 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 500, background: '#fff', borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>➕ Adicionar Responsável</h3>
              <button onClick={() => setAdicionarRespAberto(false)} aria-label="Fechar" title="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer' }}>×</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Responsável *</label>
                <select
                  value={novoResp.responsavelId}
                  onChange={(e) => setNovoResp({ ...novoResp, responsavelId: e.target.value })}
                  style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
                >
                  <option value="">Selecione uma pessoa</option>
                  {responsaveis.map((pessoa, idx) => (
                    <option key={pessoa.id || pessoa.Id || pessoa.PessoasId || pessoa.pessoasId || idx} value={pessoa.id || pessoa.Id || pessoa.PessoasId || pessoa.pessoasId || idx}>
                      {pessoa.nome || pessoa.Nome || pessoa.PessoaNome || pessoa.pessoaNome}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Horas Previstas</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={novoResp.horasPrevistas}
                  onChange={(e) => setNovoResp({ ...novoResp, horasPrevistas: e.target.value })}
                  placeholder="0.0"
                  title="As horas reais são informadas ao concluir a tarefa"
                  style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
                />
              </div>
              <button
                type="button"
                onClick={() => setNovaPessoaAberta('editar')}
                style={{ alignSelf: 'flex-start', padding: '8px 14px', backgroundColor: '#fff', color: 'rgb(235, 133, 0)', border: '1px solid rgb(235, 133, 0)', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
              >
                Adicionar nova pessoa
              </button>
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={handleAdicionarResponsavel}
                style={{ padding: '12px 30px', backgroundColor: 'rgb(235, 133, 0)', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              >
                Adicionar
              </button>
              <button
                onClick={() => {
                  setAdicionarRespAberto(false);
                  setNovoResp({ responsavelId: '', horasPrevistas: '', horasReais: '' });
                }}
                style={{ padding: '12px 30px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
